import { Button, Grid, Stack, Box } from "@mui/material";
import { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ReactSketchCanvas, ReactSketchCanvasRef } from "react-sketch-canvas";
import { StyledCircularProgress } from "../../../components/Loading/styles";
import usePortraitMode from "../../../hooks/usePortraitMode";
import { useThemeWhiteLabel } from "../../../hooks/useThemeWhiteLabel";
import { FileTypeEnum } from "../../../shared/models/enums/files.enum";
import useMailingOnBoardingService from "../../../shared/services/useMailingOnBoardingService";
import ContainerContent from "../components/ContainerContent";
import {
  ContractDrawArea,
  ContractDrawSignatureLine,
} from "../components/Contract/styles";
import { MandatoryLandscape } from "../components/MandatoryLandscape/MandatoryLandscape";
import { EMailingStepsEnum, MailingStepProps } from "../index.types";
import {
  BoldTypography,
  SubTitleInterTypography,
  TitleTypography,
  ScrollableContainer,
  ContentArea,
  ScrollbarContainer,
  ScrollbarButton,
  OrientationPopupOverlay,
  OrientationPopupContent,
  OrientationPopupTitle,
  OrientationPopupText,
  OrientationPopupButton,
} from "../styles";
import { createContractPayload, getContractDataFromStorage, saveSignedContractToStorage } from "../utils/contractUtils";

const DigitalSignatureStep = ({ setActiveStep }: MailingStepProps) => {
  const { id } = useParams();
  const isPortrait = usePortraitMode();
  const [isLoading, setIsLoading] = useState(false);
  const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  const theme = useThemeWhiteLabel();
  const { sendMailingFile, createContract } = useMailingOnBoardingService();
  const scrollButtonRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startTop, setStartTop] = useState(0);
  const [scrollButtonHeight, setScrollButtonHeight] = useState(100);
  const [maxScroll, setMaxScroll] = useState(0);
  const [showOrientationPopup, setShowOrientationPopup] = useState(true);

  const handleUpdate = (updatedPaths) => {
    setIsCanvasEmpty(updatedPaths.length === 0);
  };

  const handleSaveSignature = async () => {
    if (canvasRef.current) {
      try {
        setIsLoading(true);
        
        // Exportar assinatura como base64
        const signatureDataUrl = await canvasRef.current.exportImage("png");
        
        // Converter data URL para base64 puro (remover prefixo)
        const signatureBase64 = signatureDataUrl.split(',')[1];
        
        // Verificar se estamos assinando o segundo documento
        const isSecondDocument = localStorage.getItem('secondContractDocument') !== null;
        const order = isSecondDocument ? 2 : 1;
        
        // Recuperar dados do localStorage usando função utilitária
        const { mailingData, userData } = getContractDataFromStorage();
        
        // Criar payload do contrato com assinatura usando função utilitária
        console.log('mailingData', mailingData);
        const contractPayload = createContractPayload(mailingData, userData, id || "", signatureBase64, order);
        
        console.log(`DigitalSignature - Criando contrato assinado (order: ${order})...`);
        
        // Chamar o endpoint para criar contrato assinado
        const signedContractResponse = await createContract(contractPayload);
        
        console.log(`DigitalSignature - Contrato assinado criado com sucesso (order: ${order})`);
        
        // Salvar o contrato assinado retornado pelo backend
        if (signedContractResponse) {
          if (isSecondDocument) {
            // Salvar segundo contrato assinado
            let contractBase64;
            if (signedContractResponse instanceof ArrayBuffer) {
              const bytes = new Uint8Array(signedContractResponse);
              let binary = '';
              for (let i = 0; i < bytes.length; i++) {
                binary += String.fromCharCode(bytes[i]);
              }
              contractBase64 = btoa(binary);
            } else if (typeof signedContractResponse === 'string') {
              contractBase64 = signedContractResponse;
            } else {
              contractBase64 = signedContractResponse;
            }
            localStorage.setItem('secondSignedContractDocument', contractBase64);
            console.log('DigitalSignature - Segundo contrato assinado salvo no localStorage');
            
            // Limpar segundo documento não assinado
            localStorage.removeItem('secondContractDocument');
          } else {
            // Salvar primeiro contrato assinado
            saveSignedContractToStorage(signedContractResponse);
            
            // Limpar primeiro documento não assinado
            localStorage.removeItem('contractDocument');
          }
        }
        
        // NÃO limpar dados temporários ainda - eles são necessários para verificar se precisa de segundo documento
        // Os dados serão limpos apenas no ReadContractStep após finalização completa
        
        // Navegar para tela de leitura do contrato assinado
        setActiveStep(EMailingStepsEnum.ContractDataForm);
        
      } catch (error) {
        console.error("Erro ao salvar assinatura e criar contrato:", error);
        // Aqui você pode mostrar uma mensagem de erro para o usuário
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clearCanvas();
      setIsCanvasEmpty(true);
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    document.body.style.overflow = "hidden";
  };

  const handleTouchEnd = () => {
    document.body.style.overflow = "auto";
  };

  const handleScrollButtonMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartY(e.clientY);
    setStartTop(scrollButtonRef.current?.offsetTop || 0);
  };

  const updateScrollButtonSize = () => {
    if (!contentRef.current || !containerRef.current) return;
    
    const contentHeight = contentRef.current.scrollHeight;
    const containerHeight = containerRef.current.offsetHeight;
    
    if (contentHeight <= containerHeight) {
      setScrollButtonHeight(containerHeight);
      setMaxScroll(0);
      return;
    }

    const ratio = containerHeight / contentHeight;
    const newHeight = Math.max(30, containerHeight * ratio);
    setScrollButtonHeight(newHeight);
    setMaxScroll(containerHeight - newHeight);
  };

  useEffect(() => {
    updateScrollButtonSize();
    window.addEventListener('resize', updateScrollButtonSize);
    return () => window.removeEventListener('resize', updateScrollButtonSize);
  }, []);

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !scrollButtonRef.current || !contentRef.current || !containerRef.current) return;

    const deltaY = e.clientY - startY;
    const newTop = Math.max(0, Math.min(startTop + deltaY, maxScroll));

    scrollButtonRef.current.style.top = `${newTop}px`;

    const scrollPercentage = newTop / maxScroll;
    const contentMaxScroll = contentRef.current.scrollHeight - containerRef.current.offsetHeight;
    contentRef.current.scrollTop = scrollPercentage * contentMaxScroll;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, startY, startTop]);

  if (isPortrait)
    return (
      <>
        <MandatoryLandscape
          title="Para assinar o formulário, vire seu celular na horizontal."
          description="Assim, você terá mais espaço para sua assinatura."
        />
        {showOrientationPopup && (
          <OrientationPopupOverlay>
            <OrientationPopupContent>
              <OrientationPopupTitle>Como habilitar a rotação de tela</OrientationPopupTitle>
              <OrientationPopupText as="div">
                <p>Para assinar o formulário, é necessário virar seu celular na horizontal.</p>
                <p>Se a tela não girar automaticamente, siga estes passos:</p>
                <ol style={{ textAlign: 'left', margin: '0 0 16px 20px', padding: 0 }}>
                  <li>
                    Deslize sua tela de cima para baixo a partir da barra onde 
                    é possível visualizar a bateria e o horário do seu telefone para abrir o menu.</li>
                  <li>
                    Procure pelo ícone de rotação automática e ative-o.
                  </li>
                  <li>
                    Se não encontrar, acesse as configurações do seu aparelho e busque por 
                    &quot;rotação de tela&quot; ou 
                    &quot;orientação automática&quot;.
                  </li>
                </ol>
                <p>Assim, você poderá assinar com mais conforto!</p>
              </OrientationPopupText>
              <OrientationPopupButton onClick={() => setShowOrientationPopup(false)}>
                Entendi!
              </OrientationPopupButton>
            </OrientationPopupContent>
          </OrientationPopupOverlay>
        )}
      </>
    );

  return (
    <ContainerContent>
      <ScrollableContainer ref={containerRef}>
        <ContentArea ref={contentRef}>
          <Grid container height="100%">
            <Grid item xs={12} pb={{ xs: "16px", sm: "24px" }}>
              <TitleTypography color={theme.primaryColor}>
                Assinatura digital
              </TitleTypography>
              <SubTitleInterTypography>
                Faça sua assinatura sobre a linha abaixo. Caso erre ou não fique
                legível, clique no botão &quot;Apagar&quot; para limpar e começar novamente.
              </SubTitleInterTypography>
            </Grid>
            <Grid item xs={12}>
              <ContractDrawArea
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <ReactSketchCanvas
                  ref={canvasRef}
                  onChange={handleUpdate}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  strokeWidth={4}
                  strokeColor="black"
                />
                <Stack alignItems="center">
                  <ContractDrawSignatureLine />
                  <div>
                    <Button
                      color="primary"
                      variant="outlined"
                      size="large"
                      disabled={isCanvasEmpty}
                      onClick={handleClear}
                    >
                      Apagar
                    </Button>
                  </div>
                </Stack>
              </ContractDrawArea>
            </Grid>
            <Grid item xs={12}>
              <SubTitleInterTypography textAlign="center" mt="40px">
                Quando sua assinatura estiver satisfatória, clique no botão{" "}
                <BoldTypography>&quot;Salvar assinatura&quot;</BoldTypography> para finalizar.
              </SubTitleInterTypography>
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="center"
                gap="24px"
                mt="48px"
              >
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  disabled={isLoading}
                  onClick={() => setActiveStep(EMailingStepsEnum.ContractReading)}
                >
                  Voltar
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  disabled={isLoading || isCanvasEmpty}
                  startIcon={
                    isLoading ? <StyledCircularProgress size={16} /> : undefined
                  }
                  onClick={handleSaveSignature}
                >
                  Salvar assinatura
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </ContentArea>
        <ScrollbarContainer>
          <ScrollbarButton
            ref={scrollButtonRef}
            height={scrollButtonHeight}
            onMouseDown={handleScrollButtonMouseDown}
          />
        </ScrollbarContainer>
      </ScrollableContainer>
    </ContainerContent>
  );
};

export default DigitalSignatureStep;